import Required from 'components/Required';
import React, { ChangeEvent } from 'react';
import { FormGroup, Label, Input, Button, Row, Col } from 'reactstrap';
import UploaderMultiple from 'components/UploaderMultiple';
import DatePicker from 'components/DatePicker';
import moment from 'moment';
import { CircleAlert } from 'lucide-react';
import { Titulos } from 'types/Titulos';
import { Instituciones } from 'types/Instituciones';
import { MatriculaRequest } from 'types/Matriculas';
import { DatePickerChangeEvent } from 'components/DatePicker';

interface Props {
  handleSendForm: () => void;
  isLoading: boolean;
  disableSend: boolean;
  formValidation: Partial<Record<keyof MatriculaRequest, boolean>>;
  handleInputChange: (event: ChangeEvent<HTMLInputElement> | DatePickerChangeEvent) => void;
  titulos: Titulos[];
  data: Partial<MatriculaRequest>;
  instituciones: Instituciones[];
  validateStep: (requiredFields: string[], isForm?: boolean) => boolean;
  onBack: () => void;
}

const StepFour = ({
  handleSendForm,
  isLoading,
  disableSend,
  formValidation,
  handleInputChange,
  titulos,
  data,
  instituciones,
  validateStep,
  onBack,
}: Props) => {
  const validateStepFour = (): boolean => {
    // Lista de campos requeridos en StepFour
    const requiredFields = [
      'foto_carnet',
      'fotocopia_dni_frente',
      'fotocopia_dni_dorso',
      'antecedentes_penales',
      'titulo_institucion',
      'titulo_nombre',
      'titulo_fecha',
      'titulo_adjunto',
      'certificado_extranjero',
    ];

    return validateStep(requiredFields);
  };

  return (
    <div style={{ padding: '0 10rem 1rem 10rem' }}>
      <FormGroup>
        <Row className='mt-4 mb-2'>
          <Col sm='12'>
            <Label
              for='foto_carnet'
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
            >
              Foto carnet <Required />
            </Label>

            <div
              style={{
                width: '100%',
                border: '2px dashed gray',
                borderRadius: '10px',
                padding: '4rem',
                textAlign: 'center',
                cursor: isLoading || disableSend ? 'not-allowed' : 'pointer',
                backgroundColor: '#fafafa',
              }}
            >
              <UploaderMultiple
                name='foto_carnet'
                attachs={data.attachs}
                disabled={ isLoading || disableSend }
                required
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.85rem',
                color: '#6c757d',
                marginTop: '0.5rem',
                gap: 7,
              }}
            >
              <CircleAlert color='#9123C0' />
              Subí una foto tipo carnet (JPEG o PNG, máx 9MB) con fondo claro y
              rostro visible de frente
            </div>
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row className='mt-4 mb-2'>
          <Col sm='12'>
            <Label
              for='fotocopia_dni_frente'
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
            >
              DNI: fotocopia simple frente <Required />
            </Label>

            <div
              style={{
                width: '100%',
                border: '2px dashed gray',
                borderRadius: '10px',
                padding: '4rem',
                textAlign: 'center',
                cursor: isLoading || disableSend ? 'not-allowed' : 'pointer',
                backgroundColor: '#fafafa',
              }}
            >
              <UploaderMultiple
                name='fotocopia_dni_frente'
                attachs={data.attachs}
                disabled={ isLoading || disableSend }
                required
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.85rem',
                color: '#6c757d',
                marginTop: '0.5rem',
                gap: 7,
              }}
            >
              <CircleAlert color='#9123C0' />
              Subí tu archivo en PDF, PNG o JPEG (máx 9MB). Imagen clara y legible.
            </div>
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row className='mt-4 mb-2'>
          <Col sm='12'>
            <Label
              for='fotocopia_dni_dorso'
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
            >
              DNI: fotocopia simple dorso <Required />
            </Label>

            <div
              style={{
                width: '100%',
                border: '2px dashed gray',
                borderRadius: '10px',
                padding: '4rem',
                textAlign: 'center',
                cursor: isLoading || disableSend ? 'not-allowed' : 'pointer',
                backgroundColor: '#fafafa',
              }}
            >
              <UploaderMultiple
                name='fotocopia_dni_dorso'
                attachs={data.attachs}
                disabled={ isLoading || disableSend }
                required
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.85rem',
                color: '#6c757d',
                marginTop: '0.5rem',
                gap: 7,
              }}
            >
              <CircleAlert color='#9123C0' />
              Subí tu archivo en PDF, PNG o JPEG (máx 9MB). Imagen clara y legible.
            </div>
          </Col>
        </Row>
      </FormGroup>
      { data.pais_nac_id != 1
        ?
          <FormGroup>
            <Row className='mt-4 mb-2'>
              <Col sm='12'>
                <Label
                  for='certificado_extranjero'
                  style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
                >
                  Documento Extranjero <Required />
                </Label>

                <div
                  style={{
                    width: '100%',
                    border: '2px dashed gray',
                    borderRadius: '10px',
                    padding: '4rem',
                    textAlign: 'center',
                    cursor: isLoading || disableSend ? 'not-allowed' : 'pointer',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <UploaderMultiple
                    name='certificado_extranjero'
                    attachs={data.attachs}
                    disabled={ isLoading || disableSend }
                    accept={[
                      'image/jpeg',
                      'image/png',
                      'application/pdf',
                    ]}
                    required
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '0.85rem',
                    color: '#6c757d',
                    marginTop: '0.5rem',
                    gap: 7,
                  }}
                >
                  <CircleAlert color='#9123C0' />
                  Subí tu archivo en PDF, PNG o JPEG (máx 9MB). Imagen clara y legible.
                </div>
              </Col>
            </Row>
          </FormGroup>
        : null
      }
      <FormGroup>
        <Row className='mt-4 mb-2'>
          <Col sm='12'>
            <Label
              for='antecedentes_penales'
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
            >
              Certificado de antecedentes penales nacionales del Registro Nacional de Reincidencia <Required />
            </Label>

            <div
              style={{
                width: '100%',
                border: '2px dashed gray',
                borderRadius: '10px',
                padding: '4rem',
                textAlign: 'center',
                cursor: isLoading || disableSend ? 'not-allowed' : 'pointer',
                backgroundColor: '#fafafa',
              }}
            >
              <UploaderMultiple
                name='antecedentes_penales'
                attachs={data.attachs}
                disabled={ isLoading || disableSend }
                accept={[
                  'image/jpeg',
                  'image/png',
                  'application/pdf',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  'application/msword',
                ]}
                required
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.85rem',
                color: '#6c757d',
                marginTop: '0.5rem',
                gap: 7,
              }}
            >
              <CircleAlert color='#9123C0' />
              Subí tu archivo en PDF, PNG o JPEG (máx 9MB). Imagen clara y legible.
            </div>
          </Col>
        </Row>
      </FormGroup>
      <hr className='my-4' />
      <Col sm={12} className='text-center' style={{ marginBottom: '1.5rem' }}>
        <span
          style={{
            fontSize: '1rem',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}
        >
          Título Universitario
        </span>
      </Col>
      <FormGroup>
        <Row>
          <Col md='6'>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='titulo_institucion'
            >
              Institución <Required />
            </Label>
            <Input
              id='titulo_institucion'
              name='titulo_institucion'
              type='select'
              className='custom-select form-control'
              value={data.titulo_institucion ?? ''}
              invalid={formValidation.titulo_institucion === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend || !instituciones.length}
              required
            >
              <option value=''>Seleccione una opción...</option>
              {instituciones?.map((item: any) => {
                return (
                  <option
                    key={item.MTR018_ID_INSTITUCION_EDUCATIVA}
                    value={item.MTR018_ID_INSTITUCION_EDUCATIVA}
                  >
                    {item.MTR018_D_INSTITUCION_EDUCATIVA}
                  </option>
                );
              })}
            </Input>
          </Col>
          <Col md='6'>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='titulo_nombre'
            >
              Título <Required />
            </Label>
            <Input
              id='titulo_nombre'
              name='titulo_nombre'
              type='select'
              className='custom-select form-control'
              value={data.titulo_nombre ?? ''}
              invalid={formValidation.titulo_nombre === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend || !titulos.length}
              required
            >
              <option value=''>Seleccione una opción...</option>
              {titulos?.map((item: any) => {
                return (
                  <option
                    key={item.MTR017_ID_TITULO_GRADO}
                    value={item.MTR017_ID_TITULO_GRADO}
                  >
                    {item.MTR017_D_TITULO_GRADO}
                  </option>
                );
              })}
            </Input>
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='titulo_fecha'
            >
              Fecha obtención <Required />
            </Label>
            <DatePicker
              id='titulo_fecha'
              name='titulo_fecha'
              selected={data.titulo_fecha ?? undefined}
              dateFormat='dd/MM/yyyy'
              locale='es'
              placeholderText='Fecha obtención'
              className='date-picker form-control'
              maxDate={moment().toDate()}
              disabled={isLoading || disableSend}
              onChange={date => {
                handleInputChange({
                  target: {
                    name: 'titulo_fecha',
                    type: 'datepicker',
                    value: date,
                  },
                });
              }}
              autoComplete='off'
              showYearDropdown
              showMonthDropdown
              dropdownMode='scroll'
              required
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row className='mt-4 mb-2'>
          <Col sm='12'>
            <Label
              for='titulo_adjunto'
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
            >
              Título de grado <Required />
            </Label>

            <div
              style={{
                width: '100%',
                border: '2px dashed gray',
                borderRadius: '10px',
                padding: '4rem',
                textAlign: 'center',
                cursor: isLoading || disableSend ? 'not-allowed' : 'pointer',
                backgroundColor: '#fafafa',
              }}
            >
              <UploaderMultiple
                name='titulo_adjunto'
                attachs={data.attachs}
                disabled={ isLoading || disableSend }
                required
                multiple
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.85rem',
                color: '#6c757d',
                marginTop: '0.5rem',
                gap: 7,
              }}
            >
              <CircleAlert color='#9123C0' />
              Subí tu archivo en PDF, PNG o JPEG (máx 9MB). Imagen clara y legible.
            </div>
          </Col>
        </Row>
      </FormGroup>
      <p>
        Notas: Conforme Artículo 74 Cód. Civil y Comercial de la Nación: El domicilio
        legal es el lugar donde la presume sin admitir prueba en contra, que una persona
        reside de manera permanente para el ejercicio de sus derechos y el cumplimiento de
        sus obligaciones.
      </p>
      <p>
        Conforme Artículo 75 Cód. Civil y Comercial de la Nación las personas pueden elegir
        un domicilio para el ejercicio de los derechos y obligaciones, pudiendo constituir
        domicilio electrónico en el que se tengan por eficaces todas las notificaciones y
        comunicaciones
      </p>
      <div
        style={{ marginTop: '5rem' }}
        className='d-flex justify-content-center'
      >
        <Button
          type='button'
          className='btn-custom'
          disabled={isLoading || disableSend}
          onClick={ onBack }
        >
          Anterior
        </Button>
        <Button
          type='button'
          className='btn-custom'
          disabled={isLoading || disableSend}
          onClick={() => {
            if (validateStepFour()) handleSendForm();
          }}
        >
          Solicitar matricula
        </Button>
      </div>
    </div>
  );
};

export default StepFour;
